/**
 * Shared input helpers for request-type field groups (declarative + custom).
 */

import moment from 'moment';

// Block non-numeric typed characters on number inputs (digits and a decimal point
// only). Control/navigation keys and Ctrl/Cmd shortcuts pass through; pasted text is
// cleaned by the parser instead.
export const blockNonNumericKeyDown = (e) => {
  if (e.ctrlKey || e.metaKey || e.key.length > 1) return;
  if (!/[\d.]/.test(e.key)) e.preventDefault();
};

// $ 1,000 style display for budget / CPM InputNumbers
export const currencyProps = {
  min: 0,
  className: 'custom-input task-w--full',
  onKeyDown: blockNonNumericKeyDown,
  formatter: (value) =>
    value || value === 0 ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '',
  parser: (value) => (value ? value.replace(/[^\d.]/g, '') : ''),
};

export const urlValidator = (_, value) => {
  if (!value) return Promise.resolve();
  try {
    const url = new URL(value);
    if (!['http:', 'https:'].includes(url.protocol)) {
      return Promise.reject(new Error('Must start with http:// or https://'));
    }
    return Promise.resolve();
  } catch {
    return Promise.reject(new Error('Please enter a valid URL'));
  }
};

// --- Display formatters (read-only request-type detail views) ---------------
// Shared so the generic declarative read view and any custom one (e.g. IO) format
// values identically.

export const hasValue = (v) => v !== null && v !== undefined && v !== '';

export const fmtDate = (d) => (d ? moment(d).format('MMM D, YYYY') : null);

export const fmtMoney = (v) =>
  hasValue(v) ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : null;

export const fmtPercent = (v) => (hasValue(v) ? `${v}%` : null);

// A [start, end] pair (moment | ISO | Date) -> "Mmm D, YYYY → Mmm D, YYYY".
export const fmtDateRange = (range) => {
  if (!Array.isArray(range)) return null;
  const [start, end] = range;
  if (!hasValue(start) && !hasValue(end)) return null;
  return `${fmtDate(start) || '—'} → ${fmtDate(end) || '—'}`;
};

// Format a declarative field's stored value for read display, by descriptor type.
export const formatFieldValue = (field, value) => {
  switch (field.type) {
    case 'currency':
      return fmtMoney(value);
    case 'percent':
      return fmtPercent(value);
    case 'date':
      return fmtDate(value);
    case 'daterange':
      return fmtDateRange(value);
    case 'switch':
      return hasValue(value) ? (value ? 'Yes' : 'No') : null;
    case 'select': {
      if (!hasValue(value)) return null;
      const opt = (field.options || []).find((o) => (o.value ?? o) === value);
      return opt ? opt.label ?? opt : value;
    }
    default:
      return hasValue(value) ? value : null;
  }
};
